import { useEffect, useState } from 'react';
import api from '../api/client';
import { useFinca } from '../context/FincaContext';

/* =========================================================
   Permisos que se ocultan de la UI porque son reservados
   (solo se asignan automáticamente según el rol)
   ========================================================= */
const PERMISOS_OCULTOS = new Set([
  'configuracion.fincas',  // Solo super_admin
]);

const EMOJI_ICONO = {
  home: '🏠', droplet: '💧', calendar: '📅', activity: '📊',
  package: '📦', map: '🗺️', 'file-text': '📄', 'trending-up': '📈',
  'alert-circle': '🚨', 'alert-triangle': '⚠️', tag: '🏷️',
  stethoscope: '🩺', settings: '⚙️', folder: '📁', users: '👥',
  lock: '🔐', upload: '📥', building: '🏢', info: 'ℹ️', zap: '⚡',
  thermometer: '🌡️', clock: '🕐', wrench: '🔧', sun: '☀️',
  battery: '🔋', 'wifi-off': '📴', circle: '⚪', 'refresh-cw': '🔄',
  layers: '🗂️', 'map-pin': '📍', 'plus-circle': '➕', anchor: '⚓',
  move: '↔️', unlock: '🔓', brush: '🧹',
};

function emojiDeIcono(icono) {
  if (!icono) return '';
  return EMOJI_ICONO[icono] || '';
}

export default function Permisos() {
  const { fincas, fincaActiva, esSuperAdmin } = useFinca();
  const [fincaId, setFincaId] = useState(fincaActiva?.id || fincas[0]?.id || '');
  const [catalogo, setCatalogo] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rolSel, setRolSel] = useState(null);
  const [marcados, setMarcados] = useState(new Set());
  const [mensaje, setMensaje] = useState(null);
  const [creando, setCreando] = useState(false);
  const [nuevoRol, setNuevoRol] = useState({ nombre: '', label: '', descripcion: '' });

  useEffect(() => {
    api.get('/permisos/catalogo').then(({ data }) => {
      // ✅ Filtrar los permisos ocultos del catálogo
      const visibles = data.filter(p => !PERMISOS_OCULTOS.has(p.id));
      setCatalogo(visibles);
    });
  }, []);

  useEffect(() => {
    if (!fincaId) return;
    cargarRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaId]);

  const cargarRoles = async () => {
    try {
      const { data } = await api.get('/permisos/roles', { params: { finca_id: fincaId } });
      setRoles(data);
      if (rolSel) {
        const act = data.find(r => r.id === rolSel.id);
        if (act) seleccionarRol(act);
      }
    } catch (err) { console.error(err); }
  };

  const seleccionarRol = (rol) => {
    setRolSel(rol);
    // ✅ Filtrar los permisos ocultos por si el rol los tiene asignados
    const permisosVisibles = (rol.permisos || []).filter(p => !PERMISOS_OCULTOS.has(p));
    setMarcados(new Set(permisosVisibles));
    setMensaje(null);
  };

  const padres = catalogo.filter(p => !p.padre_id);
  const hijosDe = (padreId) => catalogo.filter(p => p.padre_id === padreId);

  /* Toggle con lógica de árbol */
  const toggle = (id) => {
    const s = new Set(marcados);
    const esPadre = padres.some(p => p.id === id);

    if (esPadre) {
      const hijos = hijosDe(id);
      const marcarTodos = !s.has(id);
      if (marcarTodos) {
        s.add(id);
        hijos.forEach(h => s.add(h.id));
      } else {
        s.delete(id);
        hijos.forEach(h => s.delete(h.id));
      }
    } else {
      const padreId = catalogo.find(p => p.id === id)?.padre_id;
      if (s.has(id)) {
        s.delete(id);
        if (padreId) s.delete(padreId);
      } else {
        s.add(id);
        if (padreId) {
          const hijos = hijosDe(padreId);
          if (hijos.every(h => s.has(h.id))) s.add(padreId);
        }
      }
    }
    setMarcados(s);
  };

  const guardar = async () => {
    if (!rolSel) return;
    try {
      await api.put(`/permisos/roles/${rolSel.id}`, {
        permisos: Array.from(marcados),
      });
      setMensaje({ tipo: 'ok', texto: 'Permisos guardados correctamente' });
      await cargarRoles();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error' });
    }
  };

  const crearRol = async (e) => {
    e.preventDefault();
    try {
      await api.post('/permisos/roles', { finca_id: fincaId, ...nuevoRol });
      setCreando(false);
      setNuevoRol({ nombre: '', label: '', descripcion: '' });
      await cargarRoles();
      setMensaje({ tipo: 'ok', texto: 'Rol creado' });
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error' });
    }
  };

  const eliminarRol = async (rol) => {
    if (!confirm(`¿Eliminar el rol "${rol.label}"?`)) return;
    try {
      await api.delete(`/permisos/roles/${rol.id}`);
      if (rolSel?.id === rol.id) setRolSel(null);
      await cargarRoles();
    } catch (err) {
      alert(err.response?.data?.error || 'Error');
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-gray-700 mb-4">Roles y Permisos</h2>

      {esSuperAdmin && (
        <div className="mb-4">
          <label className="text-sm font-medium">Finca:</label>
          <select
            value={fincaId}
            onChange={(e) => { setFincaId(e.target.value); setRolSel(null); }}
            className="ml-2 px-3 py-1.5 border rounded"
          >
            {fincas.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
          </select>
        </div>
      )}

      {mensaje && (
        <div className={`mb-4 p-3 rounded ${mensaje.tipo === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {mensaje.texto}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
        {/* Lista de roles */}
        <div className="bg-white rounded shadow p-3">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold text-sm">Roles</h3>
            <button
              onClick={() => setCreando(true)}
              className="text-xs text-primary hover:underline"
            >+ Nuevo</button>
          </div>

          <ul className="space-y-1">
            {roles.map(r => (
              <li key={r.id}>
                <button
                  onClick={() => seleccionarRol(r)}
                  className={`w-full text-left px-3 py-2 rounded text-sm flex justify-between items-center ${
                    rolSel?.id === r.id ? 'bg-blue-100 font-semibold' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className="flex flex-col">
                    <span>
                      {r.label}
                      {r.es_sistema && <span className="ml-2 text-xs text-gray-400">sistema</span>}
                    </span>
                    <span className="text-xs text-gray-500">
                      {r.usuarios_count} usuario{r.usuarios_count !== 1 ? 's' : ''}
                    </span>
                  </span>
                  {!r.es_sistema && (
                    <span
                      onClick={(e) => { e.stopPropagation(); eliminarRol(r); }}
                      className="text-red-500 text-xs"
                      title="Eliminar rol"
                    >✕</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Matriz de permisos */}
        <div className="bg-white rounded shadow p-4">
          {!rolSel ? (
            <div className="text-gray-500 text-center py-10">
              Selecciona un rol para editar sus permisos.
            </div>
          ) : (
            <>
              <h3 className="font-semibold mb-1">
                Permisos de <span className="text-primary">{rolSel.label}</span>
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                {rolSel.descripcion || 'Sin descripción'}
              </p>

              <div className="space-y-3">
                {padres.map(padre => {
                  const hijos = hijosDe(padre.id);
                  const algunosHijosMarcados = hijos.some(h => marcados.has(h.id));

                  return (
                    <div key={padre.id} className="border rounded p-3">
                      <label className="flex items-center gap-2 font-medium cursor-pointer">
                        <input
                          type="checkbox"
                          checked={marcados.has(padre.id)}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = !marcados.has(padre.id) && algunosHijosMarcados;
                            }
                          }}
                          onChange={() => toggle(padre.id)}
                        />
                        <span>
                          {emojiDeIcono(padre.icono)} {padre.label}
                        </span>
                      </label>

                      {hijos.length > 0 && (
                        <div className="ml-6 mt-2 space-y-1">
                          {hijos.map(h => (
                            <label key={h.id} className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={marcados.has(h.id)}
                                onChange={() => toggle(h.id)}
                              />
                              <span>
                                {emojiDeIcono(h.icono)} {h.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={guardar}
                  className="px-5 py-2 bg-primary text-white rounded hover:bg-blue-800 text-sm"
                >
                  Guardar cambios
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal crear rol */}
      {creando && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <form onSubmit={crearRol} className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Nuevo rol</h3>

            <label className="block mb-3">
              <span className="text-sm font-medium">Nombre interno (sin espacios)</span>
              <input
                value={nuevoRol.nombre}
                onChange={(e) => setNuevoRol({ ...nuevoRol, nombre: e.target.value })}
                className="mt-1 w-full px-3 py-2 border rounded"
                placeholder="ej: coordinador"
                required
              />
              <span className="text-xs text-gray-500">Solo minúsculas y guiones bajos.</span>
            </label>

            <label className="block mb-3">
              <span className="text-sm font-medium">Etiqueta visible</span>
              <input
                value={nuevoRol.label}
                onChange={(e) => setNuevoRol({ ...nuevoRol, label: e.target.value })}
                className="mt-1 w-full px-3 py-2 border rounded"
                placeholder="ej: Coordinador de turno"
                required
              />
            </label>

            <label className="block mb-4">
              <span className="text-sm font-medium">Descripción</span>
              <textarea
                value={nuevoRol.descripcion}
                onChange={(e) => setNuevoRol({ ...nuevoRol, descripcion: e.target.value })}
                className="mt-1 w-full px-3 py-2 border rounded"
                rows={2}
              />
            </label>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setCreando(false)} className="px-4 py-2 border rounded">
                Cancelar
              </button>
              <button type="submit" className="px-4 py-2 bg-primary text-white rounded">
                Crear
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}